import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import {
  EngineeringVectorApi,
  type EngineeringVectorApiOptions,
} from '../api';

/**
 * React dependency-inversion boundary: components consume the API only through
 * this hook/context and NEVER import ApiClient or fetch directly.
 */

const ApiContext = createContext<EngineeringVectorApi | null>(null);

export interface ApiProviderProps {
  children: ReactNode;
  /** Optional overrides (stage, identity/token providers) for tests or stages. */
  options?: EngineeringVectorApiOptions;
  /** Optional pre-built instance (primarily for testing). */
  instance?: EngineeringVectorApi;
}

export function ApiProvider({
  children,
  options,
  instance,
}: ApiProviderProps) {
  const api = useMemo(
    () => instance ?? new EngineeringVectorApi(options),
    [instance, options],
  );
  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
}

/** Returns the EngineeringVectorApi facade. Throws if used outside ApiProvider. */
export function useApi(): EngineeringVectorApi {
  const api = useContext(ApiContext);
  if (!api) {
    throw new Error('useApi must be used within an <ApiProvider>');
  }
  return api;
}
